import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_FALLBACK_LANGUAGE,
  SUPPORTED_LANGUAGES,
  ensureLocaleMode,
  getBrowserLocalStorage,
  isSupportedLanguage,
  type LocaleMode,
  readLocalePreference,
  removeStorageKeys,
  resolveInitialLanguage,
  runStorageMigrations,
  setAutoLocalePreference,
  setManualLocalePreference,
  type SupportedLanguage,
} from '@minibook/shared';

type LocaleJsonModule = { default: Record<string, unknown> };

export const BOOKING_LANGUAGE_KEY = 'bookingLanguage';
export const BOOKING_LANGUAGE_MODE_KEY = 'bookingLanguageMode';
export const STORAGE_SCHEMA_VERSION = 1;
export const STORAGE_SCHEMA_VERSION_KEY = 'webBookingStorageSchemaVersion';

const storage = getBrowserLocalStorage();
const localeModules = import.meta.glob<LocaleJsonModule>('../locales/*.json', { eager: true });
const ruFallback =
  (localeModules['../locales/ru.json']?.default as Record<string, unknown> | undefined) ?? {};

runStorageMigrations(storage, {
  schemaVersionKey: STORAGE_SCHEMA_VERSION_KEY,
  targetVersion: STORAGE_SCHEMA_VERSION,
  migrations: [
    {
      version: 1,
      migrate: (targetStorage) => {
        removeStorageKeys(targetStorage, [BOOKING_LANGUAGE_KEY, 'lang']);
      },
    },
  ],
});

ensureLocaleMode(storage, {
  modeKey: BOOKING_LANGUAGE_MODE_KEY,
  manualLanguageKey: BOOKING_LANGUAGE_KEY,
});

function getBrowserLocales(): string[] {
  if (typeof navigator === 'undefined') return [];
  const locales = Array.isArray(navigator.languages) ? navigator.languages : [];
  return [...locales, navigator.language].filter((value): value is string => typeof value === 'string' && value.length > 0);
}

function getInitialLanguage(): SupportedLanguage {
  const preference = readLocalePreference(storage, {
    modeKey: BOOKING_LANGUAGE_MODE_KEY,
    manualLanguageKey: BOOKING_LANGUAGE_KEY,
  });

  return resolveInitialLanguage({
    savedLanguage: preference.mode === 'manual' ? preference.manualLanguage : null,
    browserLocale: getBrowserLocales(),
  });
}

const resources = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((language) => {
    const module = localeModules[`../locales/${language}.json`];
    return [language, { translation: module?.default ?? ruFallback }];
  })
) as Record<SupportedLanguage, { translation: Record<string, unknown> }>;

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: DEFAULT_FALLBACK_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  pluralSeparator: '_',
});

export async function changeLanguage(lng: string): Promise<void> {
  const normalized = isSupportedLanguage(lng) ? lng : DEFAULT_FALLBACK_LANGUAGE;
  setManualLocalePreference(storage, {
    modeKey: BOOKING_LANGUAGE_MODE_KEY,
    manualLanguageKey: BOOKING_LANGUAGE_KEY,
  }, normalized);
  if (i18n.language !== normalized) {
    await i18n.changeLanguage(normalized);
  }
}

export function resetToAutoLanguageMode(): Promise<void> {
  setAutoLocalePreference(storage, {
    modeKey: BOOKING_LANGUAGE_MODE_KEY,
    manualLanguageKey: BOOKING_LANGUAGE_KEY,
  });
  const initialLanguage = getInitialLanguage();
  if (i18n.language !== initialLanguage) {
    return i18n.changeLanguage(initialLanguage).then(() => {});
  }
  return Promise.resolve();
}

export function getLanguageMode(): LocaleMode {
  return readLocalePreference(storage, {
    modeKey: BOOKING_LANGUAGE_MODE_KEY,
    manualLanguageKey: BOOKING_LANGUAGE_KEY,
  }).mode;
}

export default i18n;
export { SUPPORTED_LANGUAGES as SUPPORTED_BOOKING_LANGUAGES };
export type BookingLanguage = SupportedLanguage;
